import React, { useState, useEffect, useRef, useContext } from "react";
import { FaPhoneAlt } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import Cookies from "js-cookie";
import { ProfileContext } from "../context/ProfileContext";
import TelegramWelcomeScreen from "../components/TelegramWelcomeScreen";
import authApi from "../services/authApi";
import {
  isTelegramWebApp,
  getTelegramInitData,
  getTelegramStartParam,
  initTelegramWebApp,
  requestTelegramContact,
  requestTelegramWriteAccess,
  hasWriteAccess,
  hapticFeedback,
  parseStartParam
} from "../utils/telegramDetect";
import { isLocalhost, isUzbekistan as _isUz, isKazakhstan as _isKz } from "../utils/country";
import { setChatTokens } from "../utils/tokenRefresh";

export default function PhoneLogin() {
  const [step, setStep] = useState(1);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [showPhoneError, setShowPhoneError] = useState(false);
  const [otp, setOtp] = useState(Array(6).fill(""));
  const [countdown, setCountdown] = useState(500);
  const otpRefs = useRef([]);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [telegramLoading, setTelegramLoading] = useState(false);
  const [showWelcomeScreen, setShowWelcomeScreen] = useState(false);
  const [pendingAuthResponse, setPendingAuthResponse] = useState(null);
  const { refreshProfile } = useContext(ProfileContext);

  const [localMode, setLocalMode] = useState("MN"); // "MN", "UZ", or "KZ"

  const currentCountry = _isUz ? "UZ" : _isKz ? "KZ" : (isLocalhost ? localMode : "MN");
  const isUzbekistan = currentCountry === "UZ";
  const isKazakhstan = currentCountry === "KZ";

  // Generate Telegram bot URL based on cached redirect path
  const getTelegramBotUrl = () => {
    const redirectPath = localStorage.getItem("redirectPath");
    if (redirectPath) {
      // Parse /chat/:companyId/:jobId pattern
      const match = redirectPath.match(/\/chat\/(\d+)\/(\d+)/);
      if (match) {
        const [, companyId, jobId] = match;
        return `https://t.me/oneplace_hr_bot?start=job_${companyId}_${jobId}`;
      }
    }
    // Default: /jobs command equivalent
    return `https://t.me/oneplace_hr_bot?start=jobs`;
  };

  useEffect(() => {
    if (step === 2 && countdown > 0) {
      const interval = setInterval(() => {
        setCountdown((prev) => prev - 1);
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [step, countdown]);

  // Clear phone number when switching between modes
  useEffect(() => {
    setPhoneNumber("");
    setShowPhoneError(false);
    if (step === 2) {
      setStep(1);
      setOtp(Array(6).fill(""));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCountry]);

  // ===== TELEGRAM WEB APP AUTO-AUTH =====
  // Detect if running inside Telegram Mini App and auto-authenticate
  useEffect(() => {
    const handleTelegramWebAppAuth = async () => {
      // Check if we're inside Telegram Mini App
      if (!isTelegramWebApp()) {
        console.log("Not in Telegram Mini App, showing normal login");
        return;
      }

      console.log("=== TELEGRAM WEB APP DETECTED ===");
      console.log("initData:", getTelegramInitData());
      console.log("startParam:", getTelegramStartParam());

      // Initialize Telegram Web App (expand, ready, theme)
      initTelegramWebApp();

      // Show loading state
      setTelegramLoading(true);

      try {
        // Verify with backend and get JWT
        const response = await authApi.verifyTelegramWebApp({
          initData: getTelegramInitData(),
          country: currentCountry
        });

        console.log("Telegram Web App auth response:", response);

        if (response.token) {
          // DEBUG: Log the token being stored
          console.log("=== STORING TOKEN ===");
          console.log("Token from response:", response.token);
          console.log("Token length:", response.token?.length);

          // Store JWT token
          Cookies.set("chatToken", response.token, { expires: 30 });

          // DEBUG: Verify token was stored correctly
          const storedToken = Cookies.get("chatToken");
          console.log("Token stored in cookie:", storedToken);
          console.log("Stored token length:", storedToken?.length);
          console.log("Tokens match:", response.token === storedToken);

          // Haptic feedback for success
          hapticFeedback('success');

          // Refresh profile
          await refreshProfile();

          // Check if user needs to provide phone - show welcome screen first
          if (response.needsPhone) {
            console.log("New user - showing welcome screen before requesting permissions");
            setPendingAuthResponse(response);
            setShowWelcomeScreen(true);
            setTelegramLoading(false);
            return; // Don't navigate yet - wait for welcome screen flow
          }

          // Returning user - still request notification permission if not granted
          const canNotify = await requestTelegramWriteAccess();
          console.log("Notification permission for returning user:", canNotify ? "granted" : "denied");

          // Navigate based on startParam or default
          const startParam = getTelegramStartParam();
          const parsed = parseStartParam(startParam);

          if (parsed?.companyId && parsed?.jobId) {
            console.log(`Navigating to /chat/${parsed.companyId}/${parsed.jobId}`);
            navigate(`/chat/${parsed.companyId}/${parsed.jobId}`);
          } else {
            const redirectPath = localStorage.getItem("redirectPath") || "/chat";
            localStorage.removeItem("redirectPath");
            navigate(redirectPath);
          }
        } else {
          console.error("No token in response");
          setTelegramLoading(false);
        }
      } catch (error) {
        console.error("Telegram Web App auth error:", error);

        // CRITICAL: Clear old invalid token to force re-authentication
        Cookies.remove("chatToken");
        localStorage.removeItem("redirectPath");
        console.log("Cleared old token due to auth error");

        // Fall back to normal login UI
        setTelegramLoading(false);
        toast.error(
          isKazakhstan
            ? "Ошибка авторизации"
            : isUzbekistan
            ? "Avtorizatsiya xatosi"
            : "Нэвтрэхэд алдаа гарлаа"
        );
      }
    };

    handleTelegramWebAppAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCountry, navigate, refreshProfile]);

  const formatPhone = (value) => {
    if (isUzbekistan) {
      // Uzbekistan format: XX XXX-XX-XX (9 digits)
      const digits = value.replace(/\D/g, "").substring(0, 9);
      const first = digits.substring(0, 2);
      const second = digits.substring(2, 5);
      const third = digits.substring(5, 7);
      const fourth = digits.substring(7, 9);

      if (digits.length > 7) return `${first} ${second}-${third}-${fourth}`;
      if (digits.length > 5) return `${first} ${second}-${third}`;
      if (digits.length > 2) return `${first} ${second}`;
      return first;
    } else if (isKazakhstan) {
      // Kazakhstan format: XXX XXX-XX-XX (10 digits)
      const digits = value.replace(/\D/g, "").substring(0, 10);
      const first = digits.substring(0, 3);
      const second = digits.substring(3, 6);
      const third = digits.substring(6, 8);
      const fourth = digits.substring(8, 10);

      if (digits.length > 8) return `${first} ${second}-${third}-${fourth}`;
      if (digits.length > 6) return `${first} ${second}-${third}`;
      if (digits.length > 3) return `${first} ${second}`;
      return first;
    } else {
      // Mongolia format: XXXX-XXXX (8 digits)
      const digits = value.replace(/\D/g, "").substring(0, 8);
      const first = digits.substring(0, 4);
      const second = digits.substring(4, 8);

      if (digits.length > 4) return `${first}-${second}`;
      return first;
    }
  };

  const resendCode = () => {
    setCountdown(500);
    handleSendCode();
  };

  const handleSendCode = () => {
    const requiredLength = isUzbekistan ? 9 : (isKazakhstan ? 10 : 8);

    console.log("Phone number (raw digits):", phoneNumber);
    console.log("Length:", phoneNumber.length);
    console.log("Required length:", requiredLength);

    if (phoneNumber.length !== requiredLength) {
      setShowPhoneError(true);
      console.log("Validation failed: wrong length");
      return;
    }

    setLoading(true);

    if (isUzbekistan || isKazakhstan) {
      // UZ/KZ: These regions use Telegram - but if they enter phone, send OTP
      // Note: OTP flow is same for all countries now via hrSystemServer
      authApi.sendOtp(phoneNumber, currentCountry)
        .then(async (response) => {
          if (response.token) {
            // Direct login with bearer token (for UZ/KZ skip OTP flow)
            setChatTokens({ token: response.token, refreshToken: response.refreshToken });
            await refreshProfile();
            const redirectPath = localStorage.getItem("redirectPath") || "/chat";
            navigate(redirectPath);
          } else if (response.success) {
            setStep(2);
          } else {
            toast.error(isKazakhstan ? "Произошла ошибка" : "Xatolik yuz berdi");
            setShowPhoneError(true);
          }
        })
        .catch((error) => {
          console.error("Error authenticating:", error);
          toast.error(isKazakhstan ? "Произошла ошибка" : "Xatolik yuz berdi");
          setShowPhoneError(true);
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      // Mongolia: OTP flow via hrSystemServer
      authApi.sendOtp(phoneNumber, "MN")
        .then((response) => {
          if (response.success) {
            setStep(2);
          } else {
            toast.error("OTP илгээхэд алдаа гарлаа");
            setShowPhoneError(true);
          }
        })
        .catch((error) => {
          console.error("Error sending code:", error);
          toast.error("Алдаа гарлаа");
          setShowPhoneError(true);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  const handleVerifyOtp = () => {
    const enteredOtp = otp.join("");

    if (enteredOtp.length !== 6) {
      document.getElementById("otp-error").classList.remove("hidden");
      return;
    }

    setLoading(true);

    authApi.verifyOtp(phoneNumber, enteredOtp, currentCountry)
      .then(async (response) => {
        if (response.token) {
          setChatTokens({ token: response.token, refreshToken: response.refreshToken });
          // Fetch profile before navigating
          await refreshProfile();
          // Don't clear redirectPath here - let Profile.jsx handle it after profile completion
          const redirectPath = localStorage.getItem("redirectPath") || "/chat";
          navigate(redirectPath);
        } else {
          toast.error(isKazakhstan ? "Срок действия истек" : (isUzbekistan ? "Muddati tugagan" : "Expired"));
        }
      })
      .catch((error) => {
        console.error("Error verifying OTP:", error);
        toast.error(isKazakhstan ? "Срок действия OTP истек. Вы можете получить код снова." : (isUzbekistan ? "OTP muddati tugagan. Qaytadan kod oling." : "OTP-ийн хугацаа дууссан. Та дахин код авах боломжтой."));
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleOtpChange = (value, index) => {
    if (!/^\d?$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < 5) {
      otpRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (e, index) => {
    if (e.key === "Enter") {
      handleVerifyOtp();
      return;
    }

    if (e.key === "Backspace") {
      const newOtp = [...otp];

      if (!newOtp[index] && index > 0) {
        newOtp[index - 1] = "";
        setOtp(newOtp);
        otpRefs.current[index - 1].focus();
      } else {
        newOtp[index] = "";
        setOtp(newOtp);
      }
    }
  };

  // Handle welcome screen "Continue" button - request both permissions
  const handleWelcomeContinue = async () => {
    setTelegramLoading(true);

    try {
      // Debug: Check current permission status before requesting
      console.log("=== handleWelcomeContinue started ===");
      console.log("Current hasWriteAccess:", hasWriteAccess());

      // 1. Request notification permission FIRST
      console.log("About to request notification permission...");
      const canNotify = await requestTelegramWriteAccess();
      console.log("Notification permission result:", canNotify ? "granted" : "denied");

      if (canNotify) {
        hapticFeedback('success');
      }

      // Small delay to avoid rate limiting between popups
      await new Promise(resolve => setTimeout(resolve, 500));

      // 2. Request phone number SECOND
      console.log("About to request phone number...");
      const phone = await requestTelegramContact();

      if (phone) {
        console.log("Phone received:", phone);
        try {
          await authApi.linkTelegramPhone(phone, pendingAuthResponse.token);
          await refreshProfile();
        } catch (err) {
          console.error("Error linking phone:", err);
        }
      } else {
        console.log("User declined phone share, continuing with telegramId only");
      }

      // 3. Navigate to destination
      const startParam = getTelegramStartParam();
      const parsed = parseStartParam(startParam);

      if (parsed?.companyId && parsed?.jobId) {
        navigate(`/chat/${parsed.companyId}/${parsed.jobId}`);
      } else {
        const redirectPath = localStorage.getItem("redirectPath") || "/chat";
        localStorage.removeItem("redirectPath");
        navigate(redirectPath);
      }
    } finally {
      setTelegramLoading(false);
      setShowWelcomeScreen(false);
    }
  };

  // Show welcome screen for new Telegram users (before requesting permissions)
  if (showWelcomeScreen && isTelegramWebApp()) {
    return (
      <TelegramWelcomeScreen
        onContinue={handleWelcomeContinue}
        loading={telegramLoading}
        country={currentCountry}
      />
    );
  }

  // Show loading screen when Telegram Web App is authenticating
  if (telegramLoading && isTelegramWebApp()) {
    return (
      <div className="bg-gray-50 min-h-screen flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden w-full max-w-md p-8 text-center">
          <div className="animate-spin border-4 border-t-4 rounded-full border-gray-300 border-t-blue-600 w-12 h-12 mx-auto mb-4"></div>
          <h2 className="text-lg font-semibold text-gray-800 mb-2">
            {isKazakhstan ? "Вход через Telegram..." : isUzbekistan ? "Telegram orqali kirish..." : "Telegram-ээр нэвтэрч байна..."}
          </h2>
          <p className="text-gray-500 text-sm">
            {isKazakhstan ? "Пожалуйста, подождите" : isUzbekistan ? "Iltimos, kuting" : "Түр хүлээнэ үү"}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl overflow-hidden w-full max-w-md">
        <div className=" bg-[#000] p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold">
                {isKazakhstan ? "Добро пожаловать" : (isUzbekistan ? "Xush kelibsiz" : "Тавтай морилно уу")}
              </h1>
              <p className="text-blue-100">
                {isKazakhstan ? "Вход по номеру телефона" : (isUzbekistan ? "Telefon raqami orqali kirish" : "Утасны дугаараар нэвтрэх")}
              </p>
            </div>
            {isLocalhost && (
              <button
                onClick={() => {
                  const modes = ["MN", "UZ", "KZ"];
                  const currentIndex = modes.indexOf(localMode);
                  setLocalMode(modes[(currentIndex + 1) % modes.length]);
                }}
                className="text-xs bg-white text-black px-3 py-1 rounded-full hover:bg-gray-200 transition"
                title="Toggle between Mongolia, Uzbekistan, and Kazakhstan mode (localhost only)"
              >
                {localMode === "UZ" ? "🇺🇿 UZ" : (localMode === "KZ" ? "🇰🇿 KZ" : "🇲🇳 MN")}
              </button>
            )}
          </div>
        </div>
        <div className="p-6">
          {step === 1 && (
            <div className="fade-in">
              <h2 className="text-lg font-semibold text-gray-800 mb-2">
                {isKazakhstan ? "Введите номер телефона" : (isUzbekistan ? "Telefon raqamingizni kiriting" : "Утасны дугаараа оруулна уу")}
              </h2>
              <p className="text-gray-500 text-sm mb-4">
                {isKazakhstan ? "Отправим код подтверждения" : (isUzbekistan ? "Tasdiqlash kodini yuboramiz" : "Баталгаажуулах код илгээнэ")}
              </p>

              <div className="mb-4">
                <label className="block text-gray-700 text-sm font-medium mb-2">
                  {isKazakhstan ? "Номер телефона" : (isUzbekistan ? "Telefon raqami" : "Утасны дугаар")}
                </label>
                <div className="phone-input-container flex items-center border border-gray-300 rounded-lg px-3 py-2">
                  <FaPhoneAlt className="text-[#000]" />
                  <span className="text-gray-600 ml-2 mr-1">
                    {isUzbekistan ? "+998" : (isKazakhstan ? "+7" : "")}
                  </span>
                  <input
                    type="tel"
                    className="flex-1 border-0 focus:ring-0 focus:outline-none px-3 py-1"
                    value={formatPhone(phoneNumber)}
                    onChange={(e) => {
                      const rawDigits = e.target.value.replace(/\D/g, "");
                      setPhoneNumber(rawDigits);
                      setShowPhoneError(false);
                    }}
                    placeholder={isKazakhstan ? "701 234-56-78" : (isUzbekistan ? "90 123-45-67" : "1234-5678")}
                    disabled={loading}
                    onKeyDown={(e) => e.key === "Enter" && handleSendCode()}
                  />
                </div>
                {showPhoneError && (
                  <p className="text-red-500 text-xs mt-1">
                    {isKazakhstan ? "Введите правильный номер телефона" : (isUzbekistan ? "To'g'ri telefon raqamini kiriting" : "Зөв утасны дугаар оруулна уу")}
                  </p>
                )}
              </div>

              <button
                onClick={handleSendCode}
                onKeyDown={(e) => e.key === "Enter" && handleSendCode()}
                className="w-full bg-[#000] hover:bg-opacity-60 text-white font-medium py-2.5 px-4 rounded-lg transition duration-300"
              >
                {loading ? (
                  <>
                    <div className="animate-spin border-4 border-t-4 rounded-full border-gray-300 border-t-gray-800 w-5 h-5 inline-block mr-2"></div>
                    {isKazakhstan ? "Загрузка..." : (isUzbekistan ? "Yuklanmoqda..." : "Уншиж байна ...")}
                  </>
                ) : (
                  isKazakhstan ? "Войти" : (isUzbekistan ? "Kirish" : "Баталгаажуулах код авах")
                )}
              </button>

              {/* Telegram Bot Direct Link */}
              <div className="flex items-center my-4">
                <div className="flex-1 border-t border-gray-300"></div>
                <span className="px-3 text-gray-500 text-sm">
                  {isKazakhstan ? "или" : isUzbekistan ? "yoki" : "эсвэл"}
                </span>
                <div className="flex-1 border-t border-gray-300"></div>
              </div>

              <a
                href={getTelegramBotUrl()}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                  marginTop: '12px',
                  padding: '1rem',
                  width: '100%',
                  boxSizing: 'border-box',
                  backgroundColor: '#e3f2fd',
                  color: '#0088cc',
                  textDecoration: 'none',
                  fontSize: '1rem',
                  fontWeight: '600',
                  borderRadius: '12px',
                  border: '1px solid #bbdefb'
                }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="#0088cc">
                  <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
                </svg>
                {isKazakhstan
                  ? "Общаться через Telegram"
                  : isUzbekistan
                  ? "Telegram orqali suhbatlashish"
                  : "Telegram-аар ярилцах"}
              </a>
            </div>
          )}

          {step === 2 && (
            <div className="fade-in">
              <div className="text-center mb-6">
                <h2 className="text-lg font-semibold text-gray-800 mb-2">
                  {isKazakhstan ? "Подтвердите номер телефона" : (isUzbekistan ? "Telefon raqamingizni tasdiqlang" : "Утасны дугаараа баталгаажуулна уу")}
                </h2>
                <p className="text-gray-500 text-sm">
                  {isKazakhstan ? "Введите 6-значный код: " : (isUzbekistan ? "Yuborilgan 6 raqamli kodni kiriting: " : "Илгээсэн 6 оронтой кодыг оруулна уу: ")}
                  <span className="font-medium">
                    {isUzbekistan ? "+998 " : (isKazakhstan ? "+7 " : "")}{formatPhone(phoneNumber)}
                  </span>
                </p>
                <button
                  className="text-[#000] text-sm mt-1 hover:underline"
                  onClick={() => setStep(1)}
                >
                  {isKazakhstan ? "Изменить номер" : (isUzbekistan ? "Raqamni o'zgartirish" : "Дугаар өөрчлөх")}
                </button>
              </div>

              <div className="mb-6 flex justify-center gap-2">
                {otp.map((digit, i) => (
                  <input
                    key={i}
                    ref={(el) => (otpRefs.current[i] = el)}
                    type="number"
                    maxLength="1"
                    className="w-12 h-12 text-center border border-gray-300 rounded-lg shadow-sm text-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={digit}
                    onChange={(e) => handleOtpChange(e.target.value, i)}
                    onKeyDown={(e) => handleOtpKeyDown(e, i)}
                  />
                ))}
              </div>

              <div className="text-center mb-6">
                <p className="text-red-500 text-sm hidden" id="otp-error">
                  {isKazakhstan ? "Неверный код. Попробуйте снова." : (isUzbekistan ? "Kod noto'g'ri. Qaytadan urinib ko'ring." : "Код буруу байна. Дахин оролдоно уу.")}
                </p>
                <button
                  onClick={resendCode}
                  className="text-[#000] text-sm hover:underline"
                  disabled={countdown > 0}
                >
                  {countdown > 0
                    ? (isKazakhstan ? `Повторить через ${countdown} секунд` : (isUzbekistan ? `${countdown} soniyadan keyin qayta yuborish` : `${countdown} секундын дараа дахин илгээнэ`))
                    : (isKazakhstan ? "Отправить код повторно" : (isUzbekistan ? "Kodni qayta yuborish" : "Код дахин илгээх"))}
                </button>
              </div>

              <button
                onClick={handleVerifyOtp}
                className="w-full bg-[#000] hover:bg-opacity-60 text-white font-medium py-2.5 px-4 rounded-lg transition duration-300"
              >
                {isKazakhstan ? "Войти" : (isUzbekistan ? "Kirish" : "нэвтрэх")}
              </button>
            </div>
          )}
        </div>
      </div>
      <ToastContainer />
    </div>
  );
}
